/**
 * 血包系统 - 管理血包掉落、收集和恢复
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class HealthPackSystem {
    constructor(game) {
        this.game = game;
        this.healthPacks = new Map();

        // 血包配置
        this.healthPackLifetime = 60000; // 60秒后消失
        this.magnetRange = 8; // 自动吸附范围
        this.collectRange = 4; // 收集范围
        this.spawnCheckInterval = 60000; // 每60秒检查血包数量，减少检查频率
        this.lastSpawnCheckTime = 0;
        this.targetHealthPacks = 12; // 目标血包数量：增加到12个
        this.initialized = false; // 标记是否已初始化
    }

    update(deltaTime) {
        const now = Date.now();

        // 初始化：游戏开始时创建初始血包（只创建一次）
        if (!this.initialized) {
            // 立即创建2个血包，避免setTimeout可能的问题
            this.spawnHealthPack();
            setTimeout(() => this.spawnHealthPack(), 2000);

            this.initialized = true;
            this.lastSpawnCheckTime = now; // 重置检查时间
            return;
        }

        // 每60秒检查血包数量，如果少于目标值就补充到10个，严格控制数量
        if (now - this.lastSpawnCheckTime > this.spawnCheckInterval) {
            const currentPackCount = this.healthPacks.size;

            if (currentPackCount < this.targetHealthPacks) {
                // 只补充到目标数量，不随机
                const neededPacks = this.targetHealthPacks - currentPackCount;

                for (let i = 0; i < neededPacks; i++) {
                    // 分散生成时间，避免同时生成
                    setTimeout(() => {
                        // 再次检查当前数量，避免重复生成
                        if (this.healthPacks.size < this.targetHealthPacks) {
                            this.spawnHealthPack();
                        }
                    }, i * 2000); // 每2秒生成一个，避免过快生成
                }
            }

            this.lastSpawnCheckTime = now;
        }

        // 清理过期的血包
        const packsToRemove = [];
        this.healthPacks.forEach((pack, id) => {
            if (pack.shouldRemove) {
                packsToRemove.push(id);
            }
        });

        packsToRemove.forEach(id => {
            this.healthPacks.delete(id);
            this.game.removeGameObject(id);
        });
    }

    // 在海面随机位置生成血包
    spawnHealthPack() {
        // 尝试多次找到安全的刷新位置（不在岛屿上）
        for (let attempts = 0; attempts < 30; attempts++) {
            // 更分散、更随机的生成位置
            const angle = Math.random() * Math.PI * 2;
            // 三种距离模式随机选择：近(20-40)、中(40-70)、远(70-110)
            const distanceMode = Math.random();
            let distance;
            if (distanceMode < 0.33) {
                distance = 20 + Math.random() * 20; // 近距离
            } else if (distanceMode < 0.66) {
                distance = 40 + Math.random() * 30; // 中距离
            } else {
                distance = 70 + Math.random() * 40; // 远距离
            }

            const x = Math.cos(angle) * distance;
            const z = Math.sin(angle) * distance;
            const position = new THREE.Vector3(x, 2, z);

            // 检查是否在游戏边界内
            if (!this.game.sceneManager.isWithinGameBounds(position)) {
                continue;
            }

            // 使用射线检测确保位置在水面上，不在岛屿上
            if (this.isPositionSafeForHealthPack(position)) {
                this.createHealthPack(position);
                return;
            }
        }

        // 如果找不到安全位置，在远离岛屿的默认位置生成
        console.warn('无法找到安全的血包刷新位置，使用默认位置');
        const defaultPosition = new THREE.Vector3(-60, 2, -60);
        this.createHealthPack(defaultPosition);
    }

    isPositionSafeForHealthPack(position) {
        if (!this.game.physicsSystem) return true;

        // 使用射线检测从上方向下检测是否有岛屿碰撞
        const from = new CANNON.Vec3(position.x, 10, position.z);
        const to = new CANNON.Vec3(position.x, -5, position.z);
        
        const result = this.game.physicsSystem.raycast(from, to, {
            collisionFilterMask: 1 | 8 // 检测岛屿和建筑物
        });

        // 如果射线没有碰撞到任何物体，说明位置安全（在水面上）
        return !result.hasHit;
    }

    createHealthPack(position) {
        // 创建血包外观 - 使用父子分组：父组与物理同步，子组做旋转动画，避免被同步覆盖
        const group = new THREE.Group();
        const visual = new THREE.Group();
        group.add(visual);

        // 白色立方体底座
        const cubeGeometry = new THREE.BoxGeometry(2, 2, 2);
        const cubeMaterial = new THREE.MeshPhongMaterial({
            color: 0xFFFFFF, // 白色
            shininess: 50,
        });
        const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
        visual.add(cube);

        // 立方体顶面的红十字（平面贴图）
        const crossGeometry1 = new THREE.BoxGeometry(1.6, 0.1, 0.4);
        const crossGeometry2 = new THREE.BoxGeometry(0.4, 0.1, 1.6);

        const crossMaterial = new THREE.MeshPhongMaterial({
            color: 0xFF0000, // 红色
            emissive: 0x440000, // 自发光
        });

        const cross1 = new THREE.Mesh(crossGeometry1, crossMaterial);
        const cross2 = new THREE.Mesh(crossGeometry2, crossMaterial);

        // 放在立方体顶部
        cross1.position.y = 1.05;
        cross2.position.y = 1.05;

        visual.add(cross1);
        visual.add(cross2);

        // 移除光环（根据反馈暂不使用）

        group.position.copy(position);
        // Health pack visuals do not require shadows
        group.castShadow = false;

        // 创建物理体
        const body = this.game.physicsSystem.createProjectileBody(position, 1.5);
        body.collisionFilterGroup = 32; // 血包碰撞组
        body.collisionFilterMask = 0; // 不需要物理碰撞，用距离检测收集

        // 设置为静态
        body.type = 1; // CANNON.Body.KINEMATIC
        body.velocity.set(0, 0, 0);
        body.material.friction = 0;
        body.material.restitution = 0;

        body.userData = {
            type: 'healthpack',
            healthPackObject: null
        };

        // 创建血包对象
        const packId = `healthpack_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
        const healthPackObject = {
            id: packId,
            mesh: group,
            body: body,
            lifeTime: 0,
            maxLifeTime: this.healthPackLifetime,
            isCollected: false,
            magnetTarget: null, // 'player' 或 'enemy'
            magnetDirection: null,
            magnetTargetEnemy: null,
            lastProximityCheck: 0, // 添加检查间隔控制

            update: (deltaTime) => {
                if (healthPackObject.isCollected || healthPackObject.shouldRemove) return;

                // 同步网格和物理体
                if (group && body) {
                    this.game.physicsSystem.constructor.syncMeshWithBody(group, body);
                }

                // 旋转动画 - 只旋转子组，更慢旋转
                visual.rotation.y += deltaTime * 0.6;

                // 上下浮动动画
                const floatHeight = Math.sin(Date.now() * 0.003) * 0.5;
                group.position.y = position.y + floatHeight;

                // 移除光环动画

                // 更新生命周期
                healthPackObject.lifeTime += deltaTime * 1000;

                // 检查玩家和敌船距离，实现收集和磁铁效果
                const now = Date.now();
                if (now - healthPackObject.lastProximityCheck > 300) { // 每300ms检查一次
                    this.checkPlayerProximity(healthPackObject);
                    healthPackObject.lastProximityCheck = now;
                }

                // 生命周期结束
                if (healthPackObject.lifeTime > healthPackObject.maxLifeTime) {
                    healthPackObject.shouldRemove = true;
                }
            },

            dispose: () => {
                if (group) {
                    group.traverse((child) => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) {
                            if (Array.isArray(child.material)) {
                                child.material.forEach(mat => mat.dispose());
                            } else {
                                child.material.dispose();
                            }
                        }
                    });
                }
            }
        };

        // 设置相互引用
        body.userData.healthPackObject = healthPackObject;

        // 添加到游戏
        this.healthPacks.set(packId, healthPackObject);
        this.game.addGameObject(packId, healthPackObject);

        return healthPackObject;
    }

    // 检查玩家和敌船接近度
    checkPlayerProximity(packObject) {
        if (packObject.isCollected) return;

        // 检查玩家 - 优化：直接使用位置计算，避免创建Vector3对象
        if (this.game.player && this.game.player.body) {
            const playerBody = this.game.player.body.position;
            const packBody = packObject.body.position;

            // 简化距离计算，避免创建Vector3对象
            const dx = playerBody.x - packBody.x;
            const dz = playerBody.z - packBody.z;
            const playerDistance = Math.sqrt(dx * dx + dz * dz);

            // 玩家收集检测
            if (playerDistance <= this.collectRange) {
                this.collectHealthPack(packObject, this.game.player, 'player');
                return;
            }

            // 玩家磁铁效果
            if (playerDistance <= this.magnetRange && !packObject.magnetTarget) {
                packObject.magnetTarget = 'player';
                // 简化方向计算
                const length = playerDistance;
                packObject.magnetDirection = new THREE.Vector3(dx / length, 0, dz / length);
            }
        }

        // 检查敌船（大幅限制检查频率，避免性能问题）
        if (this.game.enemyManager && this.game.enemyManager.enemies && this.game.enemyManager.enemies.size > 0) {
            // 只检查前2个最近的敌船，避免过度计算
            const aliveEnemies = Array.from(this.game.enemyManager.enemies.values())
                .filter(enemy => enemy && enemy.body && enemy.health > 0 && !enemy.shouldRemove)
                .slice(0, 2); // 进一步减少到只检查前2个敌船

            // 优化：预先获取血包位置，避免重复计算
            const packPos = packObject.body.position;

            for (const enemy of aliveEnemies) {
                // 简化距离计算，避免创建新的Vector3对象
                const dx = enemy.body.position.x - packPos.x;
                const dz = enemy.body.position.z - packPos.z;
                const enemyDistance = Math.sqrt(dx * dx + dz * dz);

                // 敌船收集检测
                if (enemyDistance <= this.collectRange) {
                    this.collectHealthPack(packObject, enemy, 'enemy');
                    return; // 立即返回，停止检查其他敌船
                }

                // 敌船磁铁效果（如果玩家没有吸引）
                if (enemyDistance <= this.magnetRange && !packObject.magnetTarget) {
                    packObject.magnetTarget = 'enemy';
                    // 简化方向计算
                    const length = enemyDistance;
                    packObject.magnetDirection = new THREE.Vector3(dx / length, 0, dz / length);
                    packObject.magnetTargetEnemy = enemy;
                    break; // 找到一个目标就停止
                }
            }
        }

        // 执行磁铁效果
        if (packObject.magnetTarget && packObject.magnetDirection) {
            // 验证磁铁目标是否仍然有效
            let targetValid = false;

            if (packObject.magnetTarget === 'player' && this.game.player && this.game.player.body) {
                // 优化：直接计算距离，避免创建Vector3对象
                const playerBody = this.game.player.body.position;
                const packBody = packObject.body.position;
                const dx = playerBody.x - packBody.x;
                const dz = playerBody.z - packBody.z;
                const distance = Math.sqrt(dx * dx + dz * dz);

                if (distance <= this.magnetRange) {
                    targetValid = true;
                    // 更新磁铁方向
                    packObject.magnetDirection = new THREE.Vector3(dx / distance, 0, dz / distance);
                }
            } else if (packObject.magnetTarget === 'enemy' && packObject.magnetTargetEnemy &&
                      packObject.magnetTargetEnemy.body && packObject.magnetTargetEnemy.health > 0) {
                // 优化：直接计算距离，避免创建Vector3对象
                const enemyBody = packObject.magnetTargetEnemy.body.position;
                const packBody = packObject.body.position;
                const dx = enemyBody.x - packBody.x;
                const dz = enemyBody.z - packBody.z;
                const distance = Math.sqrt(dx * dx + dz * dz);

                if (distance <= this.magnetRange) {
                    targetValid = true;
                    // 更新磁铁方向
                    packObject.magnetDirection = new THREE.Vector3(dx / distance, 0, dz / distance);
                }
            }

            if (targetValid) {
                const magnetSpeed = 10;
                packObject.body.position.x += packObject.magnetDirection.x * magnetSpeed * (1/60);
                packObject.body.position.z += packObject.magnetDirection.z * magnetSpeed * (1/60);
            } else {
                // 清除无效的磁铁效果
                packObject.magnetTarget = null;
                packObject.magnetDirection = null;
                packObject.magnetTargetEnemy = null;
            }
        }
    }

    // 收集血包
    collectHealthPack(packObject, collector, collectorType) {
        if (packObject.isCollected || packObject.shouldRemove) return;

        packObject.isCollected = true;

        // 根据收集者类型恢复血量
        if (collectorType === 'player' && collector && collector.healToFull) {
            collector.healToFull();
        } else if (collectorType === 'enemy' && collector && collector.healToFull) {
            collector.healToFull();
        }

        // 创建收集特效
        this.createCollectEffect(packObject.mesh.position);

        // 标记为移除
        packObject.shouldRemove = true;

        // 立即从血包映射中移除
        this.healthPacks.delete(packObject.id);

        // 立即从游戏对象中移除
        if (this.game.removeGameObject) {
            this.game.removeGameObject(packObject.id);
        }

        // 更新UI显示
        if (this.game.rankingUI) {
            this.game.rankingUI.lastUpdate = 0; // 强制更新显示
        }
    }

    // 创建收集特效
    createCollectEffect(position) {
        // 红色十字粒子效果
        for (let i = 0; i < 10; i++) {
            const particleGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
            const particleMaterial = new THREE.MeshBasicMaterial({
                color: 0xFF3333,
                transparent: true,
                opacity: 1
            });

            const particle = new THREE.Mesh(particleGeometry, particleMaterial);
            particle.position.copy(position);

            this.game.sceneManager.add(particle);

            // 粒子动画
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 15,
                Math.random() * 10 + 5,
                (Math.random() - 0.5) * 15
            );

            let life = 1.0;
            const animateParticle = () => {
                life -= 0.04;

                particle.position.x += velocity.x * 0.02;
                particle.position.y += velocity.y * 0.02;
                particle.position.z += velocity.z * 0.02;
                velocity.y -= 0.4; // 重力

                particleMaterial.opacity = life;
                particle.scale.setScalar(life);

                if (life > 0) {
                    requestAnimationFrame(animateParticle);
                } else {
                    this.game.sceneManager.remove(particle);
                    particleGeometry.dispose();
                    particleMaterial.dispose();
                }
            };

            animateParticle();
        }
    }

    // 清理所有血包
    dispose() {
        this.healthPacks.forEach(pack => {
            if (pack.dispose) pack.dispose();
        });
        this.healthPacks.clear();
    }

    // Clear all health packs (for game restart)
    clearHealthPacks() {
        this.healthPacks.forEach((pack, id) => {
            if (pack.dispose) pack.dispose();
            this.game.removeGameObject(id);
        });
        this.healthPacks.clear();
    }
}
